import { createContext, useContext } from 'react';
import { Capacitor } from '@capacitor/core';
import {
	CapacitorSQLite,
	SQLiteConnection,
	SQLiteDBConnection,
} from '@capacitor-community/sqlite';
import * as Sentry from '@sentry/capacitor';
import { sha256 } from 'hash-wasm';
import {
	addRxPlugin,
	createRxDatabase,
	HashFunction,
	MigrationStrategies,
	RxConflictHandler,
	RxDatabase,
	RxJsonSchema,
	RxStorage,
} from 'rxdb';
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';
import { RxDBLocalDocumentsPlugin } from 'rxdb/plugins/local-documents';
import { RxDBMigrationPlugin } from 'rxdb/plugins/migration-schema';
import { RxDBUpdatePlugin } from 'rxdb/plugins/update';
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv';
import { getRxStorageIndexedDB } from 'rxdb-premium/plugins/storage-indexeddb';
import {
	getRxStorageSQLite,
	getSQLiteBasicsCapacitor,
} from 'rxdb-premium/plugins/storage-sqlite';
import { map, Observable, Subject, take, takeWhile, tap } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { Api } from '@mopla/data-models';
import { ITypedAction } from '@mopla/utils';

import { EFeatureFlagActionTypes } from './actions/featureFlagActions';
import { EFeedbacksActionTypes } from './actions/feedbacksActions';
import { LocationActionTypes } from './actions/locationActions';
import { PassengerBookingActionTypes } from './actions/passengerBookingActions';
import { PushActionTypes } from './actions/pushNotificationActions';
import { ScheduleActionTypes } from './actions/scheduleActions';
import { SubscriptionActionTypes } from './actions/subscriptionActions';
import { UserActionTypes } from './actions/userActions';
import { ofType } from './operators/ofType';
import {
	ReplicationController,
	TColReplicationDescriptor,
	TReplications,
} from './ReplicationController';

// in the browser, we want to persist data in IndexedDB, so we use the indexeddb adapter.

// const LokiIncrementalIndexedDBAdapter = require('lokijs/src/incremental-indexeddb-adapter');

export interface Action<T = any> {
	type: T;
}

interface Options<D = Dependencies> {
	dependencies: D;
}

export interface Dependencies {
	db: RxDatabase;
	api: Api;
	Sentry: typeof Sentry;
	replications?: TReplications;
}

export declare interface Effect<
	Input extends Action = any,
	Output extends Action = any
> {
	(action$: Subject<Input>, dependencies: Dependencies): Observable<Output>;
}

export interface EntityDescriptor<RxDocType = any> {
	[name: string]: {
		schema: RxJsonSchema<RxDocType>;
		migrationStrategies?: MigrationStrategies<RxDocType>;
		subscription?: any;
		authentication?: boolean;
		conflictHandler?: RxConflictHandler<RxDocType>;
	};
}

/** TODO imlement safe types for RxDatabase */
// export type TMplPassengerRxCollections = RxDatabase<
// 	CollectionsOfDatabase & {
// 		passengerBooking: RxCollection<TPassengerBookingDocType>;
// 	}
// >;

export interface BusinessLayer {
	api: Api;
	db: RxDatabase;
	addEffect: (effect: Effect) => void;
	dispatch: (action: Action) => void;
	watchActions: TWatchActionsFn;
	watchActions$: TWatchActions$Fn;
	replications?: TReplications;
	/** Used in test envs to tear down rxjs logic */
	__teardown(): void;
}

const createRxStorage = (shouldAddRxDBDebugUtils = false) => {
	let storage: RxStorage<any, any>;
	if (Capacitor.getPlatform() === 'web') {
		storage = getRxStorageIndexedDB();
	} else {
		const sqlite = new SQLiteConnection(CapacitorSQLite);
		const basics = getSQLiteBasicsCapacitor(sqlite, Capacitor);

		//basics.journalMode = Capacitor.getPlatform() === 'android' ? '' : 'WAL2'
		basics.journalMode = '';
		basics.open = async (dbName: string) => {
			const ret = await sqlite.checkConnectionsConsistency();
			const isConn = (await sqlite.isConnection(dbName, false)).result;
			let db: SQLiteDBConnection;
			if (ret.result && isConn) {
				db = await sqlite.retrieveConnection(dbName, false);
			} else {
				db = await sqlite.createConnection(
					dbName,
					false,
					'no-encryption',
					1,
					false
				);
			}

			await db.open();
			return db;
		};

		storage = getRxStorageSQLite({
			/**
			 * Different runtimes have different interfaces to SQLite.
			 * For example in node.js we have a callback API,
			 * while in capacitor sqlite we have Promises.
			 * So we need a helper object that is capable of doing the basic
			 * sqlite operations.
			 */
			sqliteBasics: basics,
		});
	}

	if (shouldAddRxDBDebugUtils) {
		storage = wrappedValidateAjvStorage({ storage });
	}

	return storage;
};

const getHashFn = async (): Promise<HashFunction | null> => {
	//@ts-expect-error crypto.subtle is not defined in the WkWebView environment
	if (window.crypto?.subtle?.digest) {
		return null;
	}

	/** TODO better to use dynamic import here, but it seems that it breaks the tree shaking.
	 * */
	return sha256;
};

export const initDatabase = async (shouldAddRxDBDebugUtils = false) => {
	addRxPlugin(RxDBCleanupPlugin);
	addRxPlugin(RxDBUpdatePlugin);
	addRxPlugin(RxDBMigrationPlugin);
	addRxPlugin(RxDBLeaderElectionPlugin);
	addRxPlugin(RxDBLocalDocumentsPlugin);

	if (shouldAddRxDBDebugUtils) {
		console.log('Adding dev plugins');

		const modules = await Promise.all([
			import('rxdb/plugins/dev-mode').then((m) => m.RxDBDevModePlugin),
			import('rxdb/plugins/json-dump').then((m) => m.RxDBJsonDumpPlugin),
		]);

		modules.forEach((module) => addRxPlugin(module));
	}

	const storage = createRxStorage(shouldAddRxDBDebugUtils);

	const hashFn = await getHashFn();

	const db = await createRxDatabase({
		name: 'moplaV2',
		multiInstance: !Capacitor.isNativePlatform(),
		eventReduce: true,
		localDocuments: true,
		storage,
		cleanupPolicy: {
			minimumDeletedTime: 1000 * 60 * 60, // hour
			minimumCollectionAge: 1000 * 60, // 1 min
			runEach: 1000 * 60 * 5, // 5 minutes
			awaitReplicationsInSync: true,
			waitForLeadership: !Capacitor.isNativePlatform(),
		},
		...(hashFn && { hashFunction: hashFn }),
	});

	return db;
};

export let epic$: Subject<Effect<Action, Action>>;
export let actionSubject$: Subject<Action>;
let initialized = false;

const dispatch = (action: Action) => {
	actionSubject$.next(action);
};

export const bootstrapBusinessLogic = async (
	api: Api,
	collections: EntityDescriptor[],
	replicationsDescriptors: TColReplicationDescriptor<any, any>[] = [],
	db: RxDatabase,
	shouldAddRxDBDebugUtils = false
): Promise<BusinessLayer> => {
	const collectionsObject = collections.reduce((obj, item) => {
		return {
			...obj,
			...item,
		};
	}, {});

	await db.addCollections(collectionsObject);

	const replications = replicationsDescriptors.reduce(
		(acc, replicationDescriptor) => {
			acc[replicationDescriptor.collectionName] = new ReplicationController(
				db,
				replicationDescriptor,
				shouldAddRxDBDebugUtils
			);

			return acc;
		},
		{} as TReplications
	);

	if (shouldAddRxDBDebugUtils) {
		/** For debug purposes */
		Object.values(replications).forEach((replicationController) => {
			replicationController.startDevLogging();
		});

		// show leadership in title
		db.waitForLeadership().then(() => {
			document.title = '♛ ' + document.title;
		});
	}

	const opts: Options = {
		dependencies: {
			db: db,
			api: api,
			Sentry,
			replications,
		},
	};

	const __teardown = initActionStream(opts);

	const bl: BusinessLayer = {
		api,
		db,
		dispatch,
		addEffect,
		watchActions,
		watchActions$,
		__teardown,
		replications,
	};

	return bl;
};

const initActionStream = (options: Options) => {
	epic$ = new Subject<Effect<Action, Action>>();
	actionSubject$ = new Subject<Action>();

	const result$ = epic$.pipe(
		mergeMap((effect) => {
			const output$ = effect(actionSubject$, options.dependencies);

			if (!output$) {
				throw new TypeError(
					`Your root Effect "${
						effect.name || '<anonymous>'
					}" does not return a stream. Double check you're not missing a return statement!`
				);
			}

			return output$;
		})
	);

	const sub = result$.subscribe((action?: Action) => {
		if (action) {
			dispatch(action);
		}
	});

	initialized = true;

	return () => {
		sub.unsubscribe();
		initialized = false;
	};
};

export const addMultipleEffects = (effects: Effect[]) => {
	effects.forEach(addEffect);
};

export const addEffect = (effect: Effect) => {
	if (!initialized) {
		throw new Error('before adding effects, initActionStream must be called');
	}
	epic$.next(effect);
};

/** TODO improve return types */
const watchActions$ = <T extends TPossibleAction>(
	types: T | T[],
	count?: number | (() => boolean)
) => {
	const _types = Array.isArray(types) ? types : [types];

	const o$ = actionSubject$.pipe(
		ofType(..._types),
		typeof count === 'function'
			? takeWhile(count)
			: count
			? take(count)
			: takeWhile(() => true)
	);

	return o$ as Subject<ITypedAction<T>>;
};

const watchActions = <T extends TPossibleAction>(p: {
	types: T | T[];
	callback: (action: ITypedAction<T>) => void;
	count?: number | (() => boolean);
}) => {
	const { types, count, callback } = p;

	addEffect(() =>
		watchActions$(types, count).pipe(
			tap((action) => callback(action as ITypedAction<T>)),
			map(() => null)
		)
	);
};

export const BusinessLayerContext = createContext<BusinessLayer>(
	{} as BusinessLayer
);

export const useBusinessLayer = () => useContext(BusinessLayerContext);

type TWatchActionsFn = typeof watchActions;
type TWatchActions$Fn = typeof watchActions$;

type TPossibleAction =
	| PushActionTypes
	| UserActionTypes
	| LocationActionTypes
	| PassengerBookingActionTypes
	| ScheduleActionTypes
	| SubscriptionActionTypes
	| ScheduleActionTypes.StartCheckIn
	| ScheduleActionTypes.StopCheckIn
	| EFeedbacksActionTypes
	| EFeatureFlagActionTypes;
